import { ICellEditorParams } from '@ag-grid-community/core'
import { LoadingOutlined } from '@ant-design/icons'
import { Button, Divider, Typography } from 'antd'
import Select, { LabeledValue, SelectProps } from 'antd/lib/select'
import { debounce, orderBy, uniqBy } from 'lodash'
import React, { useCallback, useImperativeHandle, useMemo, useState } from 'react'
import { useSearchUnspscCommodityQuery } from 'src/api/hooks/basket-smart-rules'

export const UnspscCommoditySelector: React.FunctionComponent<SelectProps<LabeledValue | LabeledValue[]>> = ({
  style,
  ...props
}) => {
  const [searchText, setSearchText] = useState('')

  const { data, loading } = useSearchUnspscCommodityQuery({
    variables: { input: { search: searchText } },
    skip: !searchText,
  })

  const unspscCommodities = useMemo(
    () =>
      uniqBy(
        [
          ...(props.value ? [props.value].flat() : []),
          ...orderBy(data && data.searchUnspscCommodity.data, r => r.commodityTitle).map(i => ({
            label: i.commodityTitle,
            value: i.commodity,
          })),
        ],
        'value',
      ),
    [data, props.value],
  )

  const dropdownRender = useCallback(
    (menu: React.ReactNode) => {
      return (
        <>
          {menu}
          {loading && (
            <Typography.Text style={{ margin: '0px 0 0px 8px' }} type="secondary">
              Searching ...
            </Typography.Text>
          )}
          {data?.searchUnspscCommodity &&
            data?.searchUnspscCommodity.total > data?.searchUnspscCommodity.data.length && (
              <>
                <Divider style={{ margin: '8px 0' }} />
                <Button type="link" style={{ whiteSpace: 'nowrap', padding: '0 8px 4px' }}>
                  There are {data?.searchUnspscCommodity.total - data?.searchUnspscCommodity.data.length} more results
                  available
                </Button>
              </>
            )}
        </>
      )
    },
    [data?.searchUnspscCommodity, loading],
  )

  return (
    <Select
      onSearch={debounce(setSearchText, 800)}
      loading={loading}
      labelInValue
      showSearch
      filterOption={false}
      showArrow={false}
      defaultActiveFirstOption={false}
      dropdownMatchSelectWidth={false}
      notFoundContent={null}
      placeholder="Start typing to search"
      style={{ width: '100%', ...style }}
      clearIcon={loading ? <LoadingOutlined spin /> : undefined}
      dropdownRender={dropdownRender}
      {...props}>
      {unspscCommodities?.map(i => (
        <Select.Option key={i.value} value={i.value} label={i.label}>
          {`${i.label} (${i.value})`}
        </Select.Option>
      ))}
    </Select>
  )
}

export const UnspscCommoditySelectorAgWrapper = React.forwardRef<{}, ICellEditorParams>((params, ref) => {
  const [unspscCommodityCode, setUnspscCommodityCode] = useState<LabeledValue | undefined>(
    params.data?.unspscCommodityCode && {
      value: params.data.unspscCommodityCode,
      label: params.data.unspscCommodityTitle,
    },
  )

  useImperativeHandle(ref, () => ({
    getValue: () => unspscCommodityCode,
  }))

  return (
    <UnspscCommoditySelector
      allowClear
      value={unspscCommodityCode}
      onChange={v => {
        setUnspscCommodityCode(v as LabeledValue)
      }}
    />
  )
})
